import { liteClient } from 'algoliasearch/lite';
import React from 'react';

declare const Drupal: { t: (s: string, args?: Record<string, string>) => string };
import {
  InstantSearch,
  Hits,
  SearchBox,
  Pagination,
  Configure,
  ClearRefinements,
  useHierarchicalMenu,
  useRefinementList,
  useHits,
  useInstantSearch
} from 'react-instantsearch';

import SearchResult from './Components/SearchResult';

// -----------------------------------------------------------------------------
// Search client & index
// -----------------------------------------------------------------------------

const searchClient = liteClient(
  'TVH64D3HXT',
  '8d717120eb996adbd1ac7500cb5d08df'
);

function getIndexName() {
  const baseIndex = window.location.hostname.includes('lambo.nl')
    ? 'products_production'
    : 'products_test';
  return baseIndex;
}

// -----------------------------------------------------------------------------
// Filter widgets: data from Algolia hooks, or from fallback when 0 results
// -----------------------------------------------------------------------------

const HIERARCHICAL_ATTRIBUTES = [
  'field_courses_processed.lvl0',
  'field_courses_processed.lvl1'
];
const FACET_ATTRIBUTES = [
  'field_audience',
  'field_level',
  'entity_bundle_processed',
  ...HIERARCHICAL_ATTRIBUTES
];

const SORT_ALPHA = ['name:asc'];

function CustomHierarchicalMenu({ attributes, fallbackItems }) {
  const { items, refine } = useHierarchicalMenu({ attributes, sortBy: SORT_ALPHA });
  const list = (items.length > 0 ? items : fallbackItems) ?? [];

  const renderList = (list) => (
    <ul>
      {list.map((item) => (
        <li key={item.label}>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              refine(item.value);
            }}
            style={{ fontWeight: item.isRefined ? '600' : '' }}
          >
            {item.label}
            <span className="ais-RefinementList-Count">({item.count})</span>
          </a>
          {item.data?.length > 0 && item.isRefined && renderList(item.data)}
        </li>
      ))}
    </ul>
  );

  if (list.length === 0) {
    return <p className="ais-RefinementList-noResults">{Drupal.t('No options for current search')}</p>;
  }
  return renderList(list);
}

function CustomRefinementList({ attribute, fallbackItems }) {
  const { items, refine } = useRefinementList({ attribute, sortBy: SORT_ALPHA });
  const list = (items.length > 0 ? items : fallbackItems) ?? [];

  if (list.length === 0) {
    return <p className="ais-RefinementList-noResults">{Drupal.t('No options for current search')}</p>;
  }

  return (
    <div className="ais-RefinementList">
      <ul>
        {list.map((item) => (
          <li
            key={item.label}
            className={['ais-RefinementList-item', item.isRefined && 'ais-RefinementList-item--selected'].filter(Boolean).join(' ')}
          >
            <label className="ais-RefinementList-label">
              <input
                className="ais-RefinementList-checkbox"
                type="checkbox"
                checked={item.isRefined}
                onChange={() => refine(item.value)}
              />
              {item.label}
              <span className="ais-RefinementList-Count">({item.count})</span>
            </label>
          </li>
        ))}
      </ul>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Results: no-results message or hits + pagination
// -----------------------------------------------------------------------------

function CustomHitsWithNoResults({ hitComponent }) {
  const { hits } = useHits();

  if (hits.length === 0) {
    return (
      <div className="ais-Hits ais-Hits--no-results">
        <div className="no-results-message">
          <p>{Drupal.t('No results found for your search')}.</p>
          <p>{Drupal.t('Try other search terms or adjust the filters')}.</p>
        </div>
      </div>
    );
  }

  return <Hits hitComponent={hitComponent} />;
}

function ConditionalPagination() {
  const { hits } = useHits();
  return hits.length > 0 ? <Pagination /> : null;
}

// -----------------------------------------------------------------------------
// Fallback facets: when there are 0 results Algolia returns no facets; one extra
// query with empty query to fetch all facet values (refine still updates main search)
// -----------------------------------------------------------------------------

function facetsToRefinementItems(facets, attribute, selected) {
  const values = facets?.[attribute];
  if (!values || typeof values !== 'object') return [];
  const chosen = selected?.[attribute] ?? [];
  return Object.entries(values)
    .map(([value, count]) => ({
      label: value,
      value,
      count,
      isRefined: chosen.includes(value)
    }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
}

function facetsToHierarchicalItems(facets, selectedLvl0, selectedLvl1) {
  const lvl0 = facets?.['field_courses_processed.lvl0'];
  const lvl1 = facets?.['field_courses_processed.lvl1'];
  if (!lvl0 || typeof lvl0 !== 'object') return [];
  const sortByLabel = (a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
  return Object.entries(lvl0)
    .map(([label, count]) => {
      const value = label;
      const lvl1Entries = lvl1 && typeof lvl1 === 'object'
        ? Object.entries(lvl1).filter(([k]) => k.startsWith(value + ' > '))
        : [];
      const data = lvl1Entries
        .map(([key, childCount]) => ({
          label: key.split(' > ').slice(1).join(' > '),
          value: key,
          count: childCount,
          isRefined: selectedLvl1.includes(key),
          data: []
        }))
        .sort(sortByLabel);
      return {
        label,
        value,
        count,
        isRefined: selectedLvl0.includes(value),
        data
      };
    })
    .sort(sortByLabel);
}

// -----------------------------------------------------------------------------
// Left panel: always show all filters; when 0 results, data comes from fallback query
// -----------------------------------------------------------------------------

function FiltersPanel({ indexName }) {
  const { results, indexUiState } = useInstantSearch();
  const [fallbackFacets, setFallbackFacets] = React.useState(null);

  const noResults = results != null && !results.__isArtificial && results.nbHits === 0;

  // Only when 0 results: one extra search with query '' to fetch facets
  React.useEffect(() => {
    if (!noResults || !indexName) {
      setFallbackFacets(null);
      return;
    }
    let cancelled = false;
    searchClient
      .search([{ indexName, query: '', hitsPerPage: 0, facets: FACET_ATTRIBUTES }])
      .then(({ results: res }) => {
        if (!cancelled && res?.[0]?.facets) setFallbackFacets(res[0].facets);
      })
      .catch(() => setFallbackFacets(null));
    return () => { cancelled = true; };
  }, [noResults, indexName]);

  const refinementList = indexUiState?.refinementList ?? {};
  const hierarchicalMenu = indexUiState?.hierarchicalMenu ?? {};
  const selLvl0 = hierarchicalMenu?.['field_courses_processed.lvl0'] ?? [];
  const selLvl1 = hierarchicalMenu?.['field_courses_processed.lvl1'] ?? [];

  const fallbackCourse = noResults ? facetsToHierarchicalItems(fallbackFacets, selLvl0, selLvl1) : undefined;
  const fallbackAudience = noResults ? facetsToRefinementItems(fallbackFacets, 'field_audience', refinementList) : undefined;
  const fallbackLevel = noResults ? facetsToRefinementItems(fallbackFacets, 'field_level', refinementList) : undefined;
  const fallbackProductType = noResults ? facetsToRefinementItems(fallbackFacets, 'entity_bundle_processed', refinementList) : undefined;

  return (
    <>
      <ClearRefinements excludedAttributes={[]} translations={{ resetButtonText: Drupal.t('Reset filters') }} />

      <div className="courses_filter">
        <h4>{Drupal.t('Course')}</h4>
        <CustomHierarchicalMenu attributes={HIERARCHICAL_ATTRIBUTES} fallbackItems={fallbackCourse} />
      </div>

      <h4>{Drupal.t('Audience')}</h4>
      <CustomRefinementList attribute="field_audience" fallbackItems={fallbackAudience} />

      <h4>{Drupal.t('Level')}</h4>
      <CustomRefinementList attribute="field_level" fallbackItems={fallbackLevel} />

      <h4>{Drupal.t('Product type')}</h4>
      <CustomRefinementList attribute="entity_bundle_processed" fallbackItems={fallbackProductType} />
    </>
  );
}

// -----------------------------------------------------------------------------
// Main: one InstantSearch query; when 0 hits FiltersPanel runs one extra query for facets
// -----------------------------------------------------------------------------

const Webshop = () => {
  const indexName = getIndexName();
  const [showFiltersMobile, setShowFiltersMobile] = React.useState(false);

  return (
    <div>
      <InstantSearch indexName={indexName} searchClient={searchClient} routing={true}>
        <SearchBox placeholder={Drupal.t('Search our catalog...')} submit={<img src="" alt="" />} />

        <div className="also-available">
          {Drupal.t('See also:')} <a href="/bulk-bestellen">{Drupal.t('bulk order')}</a>
        </div>

        <div className="ais-InstantSearch">
          <div className="toggle-search-filter">
            <a href="#" onClick={() => setShowFiltersMobile((v) => !v)} style={{ cursor: 'pointer' }}>
              {showFiltersMobile ? Drupal.t('Hide filters') : Drupal.t('Show filters')}
            </a>
          </div>

          <div className={showFiltersMobile ? 'left-panel' : 'left-panel filters-mobile-collapsed'}>
            <FiltersPanel indexName={indexName} />
          </div>

          <div className="right-panel">
            <CustomHitsWithNoResults hitComponent={SearchResult} />
            <ConditionalPagination />
          </div>

          <Configure hitsPerPage={12} />
        </div>
      </InstantSearch>
    </div>
  );
};

export default Webshop;
